import interact from "interactjs";
import {
  DeleteIcon,
  UpIcon,
  DownIcon,
  AddIcon,
  PillarIcon,
  DragHandleIcon,
} from "../../icons/icons";
import BtnTooltip from "../../tooltips/btnTooltip";

const css = {
  /**
   * Tool's classes
   */

  mainSectionContainer: "entity-section-container",
  sectionTitleContainer: "entity-section-title-container",
  sectionTitle: "entity-section-title",
  sectionDescriptionContainer: "entity-description-container",
  sectionDescription: "entity-description",

  entitiesContainer: "entity-section-entities-container",
  entityItem: "entity-section-item",
  entityItemTitle: "entity-section-item-title",
  entityItemDragBtn: "entity-section-item-drag-btn",
  entityItemUpBtn: "entity-section-item-up-btn",
  entityItemDownBtn: "entity-section-item-down-btn",
  entityItemDeleteBtn: "entity-section-item-delete-btn",
  entityItemDragging: "is-dragging",

  addEntityContainer: "entity-section-add-entity-container",
  addEntityBtn: "entity-section-add-entity-btn",

  blockBtn: "block-btn",
  blockBtnSmall: "small",
  blockBtnLarge: "large",
  largeIcon: "large-icon",
  xlIcon: "xl-icon",
  xxlIcon: "xxl-icon",
  clear: "clear",
  focusVisibleBtn: "focus-visible-btn",
  tuneWrapper: "editor-block-tune",
  tuneTitle: "editor-block-tune-title",
  tuneText: "editor-block-tune-text",
  tuneInput: "editor-block-tune-input",
  editorFont: "editor-font",
  editorBlockModal: "editor-block-modal",
  editorBlockModalTitle: "editor-block-modal-title",
  editorBlockModalLabel: "editor-block-modal-label",
  editorBlockModalText: "editor-block-modal-text",
  editorBlockModalInput: "editor-block-modal-input",
  adminFont: "admin-font",
};

export default class TrainingSection {
  static get toolbox() {
    return {
      icon: PillarIcon,
      title: "Training Section",
    };
  }

  static get isReadOnlySupported() {
    return true;
  }

  constructor({ data, config, api, readOnly, block }) {
    this.api = api;
    this.readOnly = readOnly;
    this.block = block;

    /**
     * Tool's settings passed from Editor
     *
     * @type {TitleConfig}
     * @private
     */
    this._settings = config;

    this._data = {
      title: data.title || "",
      description: data.description || "",
      entities: data.entities || [],
    };
  }

  getTitle() {
    const container = document.createElement("div");
    const tag = document.createElement("h4");

    container.classList.add(css.sectionTitleContainer);
    tag.classList.add(css.sectionTitle);

    if (!this.readOnly) {
      tag.setAttribute("contenteditable", "true");
    }

    tag.innerHTML = this._data.title;

    container.appendChild(tag);

    return container;
  }

  getDescription() {
    const container = document.createElement("div");
    const tag = document.createElement("p");

    container.classList.add(css.sectionDescriptionContainer);
    tag.classList.add(css.sectionDescription);

    if (!this.readOnly) {
      tag.setAttribute("contenteditable", "true");
    }

    tag.innerHTML = this._data.description;
    container.appendChild(tag);
    return container;
  }

  entityItem(item, index) {
    const container = document.createElement("div");
    const title = document.createElement("p");
    const dragHandle = document.createElement("button");
    const deleteBtn = document.createElement("button");

    container.classList.add(css.entityItem);
    title.classList.add(css.entityItemTitle);
    dragHandle.classList.add(
      css.entityItemDragBtn,
      css.blockBtn,
      css.blockBtnSmall,
    );
    deleteBtn.classList.add(
      css.entityItemDeleteBtn,
      css.blockBtn,
      css.blockBtnSmall,
      css.clear,
    );

    BtnTooltip(deleteBtn, "Remove resource");

    title.innerHTML = item.title;
    dragHandle.innerHTML = DragHandleIcon;
    deleteBtn.innerHTML = DeleteIcon;

    if (!this.readOnly) {
      deleteBtn.onclick = () => {
        this._data.entities.splice(index, 1);
        this.refreshEntities();
        this.block.dispatchChange();
      };
    }

    container.appendChild(dragHandle);
    container.appendChild(title);
    // container.appendChild(upBtn);
    // container.appendChild(downBtn);
    container.appendChild(deleteBtn);

    return container;
  }

  refreshEntities() {
    this._entitiesContainer.innerHTML = "";
    this._data.entities.forEach((item, index) => {
      this._entitiesContainer.appendChild(this.entityItem(item, index));
    });
  }

  initSortable() {
    const container = this._entitiesContainer;
    let dy = 0;
    let startIndex = -1;

    this._interactable = interact(`.${css.entityItem}`, {
      context: container,
    }).draggable({
      allowFrom: `.${css.entityItemDragBtn}`,
      autoScroll: true,
      listeners: {
        start: (event) => {
          const item = event.target;
          startIndex = Array.from(container.children).indexOf(item);
          dy = 0;
          item.classList.add(css.entityItemDragging);
        },

        move: (event) => {
          const item = event.target;
          dy += event.dy;
          item.style.transform = `translateY(${dy}px)`;

          const swapWith = (sibling, before) => {
            const topBefore = item.getBoundingClientRect().top;
            container.insertBefore(
              before ? item : sibling,
              before ? sibling : item,
            );
            const topAfter = item.getBoundingClientRect().top;
            // Keep the item visually under the pointer after its layout slot changes
            dy += topBefore - topAfter;
            item.style.transform = `translateY(${dy}px)`;
          };

          const rect = item.getBoundingClientRect();
          const center = rect.top + rect.height / 2;

          const next = item.nextElementSibling;
          if (next) {
            const r = next.getBoundingClientRect();
            if (center > r.top + r.height / 2) return swapWith(next, false);
          }

          const prev = item.previousElementSibling;
          if (prev) {
            const r = prev.getBoundingClientRect();
            if (center < r.top + r.height / 2) return swapWith(prev, true);
          }
        },

        end: (event) => {
          const item = event.target;
          const newIndex = Array.from(container.children).indexOf(item);

          item.classList.remove(css.entityItemDragging);
          item.style.transform = "";

          if (newIndex !== -1 && newIndex !== startIndex) {
            const entities = this._data.entities;
            const [moved] = entities.splice(startIndex, 1);
            entities.splice(newIndex, 0, moved);
            this.refreshEntities();
            this.block.dispatchChange();
          }
        },
      },
    });
  }

  getEntities() {
    this._entitiesContainer = document.createElement("div");
    this._entitiesContainer.classList.add(css.entitiesContainer);

    this._data.entities.forEach((item, index) => {
      this._entitiesContainer.appendChild(this.entityItem(item, index));
    });

    if (!this.readOnly) this.initSortable();

    return this._entitiesContainer;
  }

  addEntities() {
    const container = document.createElement("div");
    const tag = document.createElement("button");

    BtnTooltip(tag, "Open resource selector");
    container.classList.add(css.addEntityContainer);
    tag.classList.add(css.addEntityBtn, css.blockBtn, css.blockBtnLarge);

    tag.innerHTML = `${AddIcon} Select resource`;

    tag.onclick = () => {
      const blockIndex = this.api.blocks.getCurrentBlockIndex();
      const block = this.api.blocks.getBlockByIndex(blockIndex);

      const data = {
        title: this.title.querySelector(`.${css.sectionTitle}`).innerHTML,
        description: this.description.querySelector(
          `.${css.sectionDescription}`,
        ).innerHTML,
        entities: this._data.entities,
      };
      this._settings.showEntitySelector({
        details: block,
        data: data,
      });
    };

    container.appendChild(tag);
    return container;
  }

  render() {
    const container = document.createElement("div");
    container.classList.add(css.mainSectionContainer);

    this.title = this.getTitle();
    this.description = this.getDescription();

    container.appendChild(this.title);
    container.appendChild(this.description);
    container.appendChild(this.getEntities());
    container.appendChild(this.addEntities());

    return container;
  }

  save() {
    const data = {
      title: this.title.querySelector(`.${css.sectionTitle}`).innerHTML,
      description: this.description.querySelector(`.${css.sectionDescription}`)
        .innerHTML,
      entities: this._data.entities,
    };
    return data;
  }

  destroy() {
    if (this._interactable) this._interactable.unset();
  }
}